import { TLDR_POINTS, type Tldr as TldrDoc } from '../ask.js';

/**
 * The project's landing (prd-deltas §24, §26): four sentences, one each for the idea,
 * the flow, the framework and language choices, and the infrastructure, written from
 * its documents and kept per branch (§27). The idea is set largest — it is
 * what the application is — and the three choices beneath it, each under its name.
 */

export type TldrState =
  | { status: 'loading'; writing?: boolean }
  | { status: 'ready'; tldr: TldrDoc; at: number }
  | { status: 'error'; message: string };

export function Tldr({
  name,
  state,
  onRegenerate,
  onLift,
  liftTitle,
}: {
  name: string;
  state: TldrState | undefined;
  onRegenerate: () => void;
  /** Offered when there is nothing to summarise yet: read the repository first. */
  onLift?: () => void;
  liftTitle: string;
}) {
  return (
    <div className="page tldr">
      <div className="tldr-body">
        <p className="page-kicker">{name}</p>
        {!state || state.status === 'loading' ? (
          <p className="tldr-text is-waiting" aria-live="polite">
            {state?.status === 'loading' && state.writing ? 'Writing it again…' : 'Reading the application…'}
          </p>
        ) : state.status === 'error' ? (
          <>
            <p className="tldr-text is-quiet">{state.message}</p>
            <p className="page-note">
              <button type="button" className="text-button" onClick={onRegenerate}>Try again</button>
            </p>
          </>
        ) : Object.keys(state.tldr.points).length === 0 ? (
          <>
            <p className="tldr-text is-quiet">{state.tldr.message ?? 'There is nothing to summarise yet.'}</p>
            {onLift ? (
              <p className="page-note">
                <button type="button" className="text-button" onClick={onLift}>{liftTitle}</button>
              </p>
            ) : null}
          </>
        ) : (
          <>
            <dl className="tldr-points">
              {TLDR_POINTS.filter(({ key }) => state.tldr.points[key]).map(({ key, label }) => (
                <div key={key} className={`tldr-point is-${key}`}>
                  <dt>{label}</dt>
                  <dd>{state.tldr.points[key]}</dd>
                </div>
              ))}
            </dl>
            {state.tldr.freshness?.suggest === 'rescan' ? (
              // Worth writing again (§27): said where it cannot be missed, with the way to do it.
              <p className="tldr-stale">
                <span>{state.tldr.freshness.message}</span>
                <button type="button" className="tldr-rewrite" onClick={onRegenerate}>Write again</button>
              </p>
            ) : null}
            <p className="page-note">
              Written {ago(state.at)} from {state.tldr.basis.length} document
              {state.tldr.basis.length === 1 ? '' : 's'}
              {state.tldr.freshness && state.tldr.freshness.suggest !== 'rescan' ? (
                <>
                  <span className="page-note-sep" aria-hidden="true">·</span>
                  {state.tldr.freshness.suggest === 'fresh' ? 'Up to date' : state.tldr.freshness.message}
                </>
              ) : null}
              {state.tldr.freshness?.suggest === 'rescan' ? null : (
                <>
                  <span className="page-note-sep" aria-hidden="true">·</span>
                  <button type="button" className="text-button" onClick={onRegenerate}>Write again</button>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function ago(at: number): string {
  const minutes = Math.round((Date.now() - at) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return 'a minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return days === 1 ? 'yesterday' : `${days} days ago`;
  return `on ${new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}`;
}
